import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { animated, useSpring } from 'react-spring';

import { getCurrentLanguage } from 'utils/translation';

import { Col, Hidden, Visible } from 'components/Grid';

import {
  Container,
  Row,
  Name,
  Description,
  Image,
  GetStarted
} from './styles';

const Feature = ({ isInverted, isGetStarted, name, color }) => {
  const ref = useRef();
  const { t } = useTranslation();
  const currentLanguage = getCurrentLanguage();

  const [animation, setAnimation] = useSpring(() => ({
    opacity: 0,
    left: -100,
    right: 100,
  }));

  const transform = (offset) => (
    `translateX(${offset}%)`
  );

  const handleScroll = () => {
    const rect = ref.current.getBoundingClientRect();
    const offset = window.innerHeight - rect.top;

    if (offset > 0) {
      const opacity = offset * 0.003;
      const left = -100 + (offset * 0.3);
      const right = 100 - (offset * 0.3);

      setAnimation({
        opacity: opacity < 1 ? opacity : 1,
        left: left < 0 ? left : 0,
        right: right > 0 ? right : 0,
      });
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  });

  return (
    <Container ref={ref}>
      <Row align="center" gutterWidth={85}>
        <Col xs={12} sm={6} push={{ sm: isInverted ? 6 : 0 }}>
          <animated.div style={{
            opacity: animation.opacity,
            transform: !isInverted
              ? animation.left.interpolate(transform)
              : animation.right.interpolate(transform)
          }}>
            <Row>
              <Hidden xs>
                <Name color={color}>{t(`FEATURES.${name}.NAME`)}</Name>
                <Description>{t(`FEATURES.${name}.DESCRIPTION`)}</Description>
                {isGetStarted && (
                  <GetStarted isInverted>{t('FEATURES.COMMON.GET_STARTED')}</GetStarted>
                )}
              </Hidden>
              <Visible xs>
                <Col bottom={50}>
                  <Name.xs color={color}>{t(`FEATURES.${name}.NAME`)}</Name.xs>
                  <Description.xs>{t(`FEATURES.${name}.DESCRIPTION`)}</Description.xs>
                  {isGetStarted && (
                    <GetStarted isInverted>{t('FEATURES.COMMON.GET_STARTED')}</GetStarted>
                  )}
                </Col>
              </Visible>
            </Row>
          </animated.div>
        </Col>
        <Col xs={12} sm={6} pull={{ sm: isInverted ? 6 : 0 }}>
          <animated.div style={{
            opacity: animation.opacity,
            transform: !isInverted
              ? animation.right.interpolate(transform)
              : animation.left.interpolate(transform)
          }}>
            <Image
              src={`/assets/images/features/${name.toLowerCase()}_${currentLanguage}.png`}
              alt={name}
            />
          </animated.div>
        </Col>
      </Row>
    </Container>
  );
};

Feature.propTypes = {
  name: PropTypes.string.isRequired,
  isInverted: PropTypes.bool,
  isGetStarted: PropTypes.bool,
  color: PropTypes.string.isRequired,
};

export default Feature;
